'use client'

import { useWallet } from '@/providers/WalletContext'
import { useQuery } from '@tanstack/react-query'
import bs58 from 'bs58'
import { useCallback, useState } from 'react'

const PROOF_DEEP_LINK_BASE = 'https://dflow.net/proof'

async function fetchKycStatus(address: string): Promise<{ verified: boolean }> {
	const response = await fetch(`/api/kyc/verify?address=${address}`)
	if (!response.ok) {
		throw new Error('Failed to check KYC status')
	}
	return response.json()
}

export function useProofKyc(solanaAddress: string | undefined) {
	const { solanaSigner } = useWallet()
	const [isRedirecting, setIsRedirecting] = useState(false)

	const { data, isLoading } = useQuery({
		queryKey: ['proofKyc', solanaAddress],
		queryFn: () => fetchKycStatus(solanaAddress as string),
		enabled: !!solanaAddress,
		staleTime: 5 * 60 * 1000,
		retry: 1,
	})

	const startVerification = useCallback(async () => {
		if (!(solanaAddress && solanaSigner)) return

		setIsRedirecting(true)
		try {
			const timestamp = Date.now()
			const message = `Proof KYC verification: ${timestamp}`
			const messageBytes = new TextEncoder().encode(message)

			const signatureBytes = await solanaSigner.signMessage(messageBytes)
			const signature = bs58.encode(signatureBytes)

			const params = new URLSearchParams({
				wallet: solanaAddress,
				signature,
				timestamp: timestamp.toString(),
			})
			params.set('redirect_uri', window.location.href)

			window.location.href = `${PROOF_DEEP_LINK_BASE}?${params.toString()}`
		} catch (error) {
			console.error('Failed to start KYC verification:', error)
			setIsRedirecting(false)
		}
	}, [solanaAddress, solanaSigner])

	return {
		verified: data?.verified ?? false,
		isLoading,
		startVerification,
		isRedirecting,
	}
}
